import React, { useState, useEffect } from 'react';
import styles from './GuidedPromptFlow.module.css';
import { GuidedPromptFlowProps } from './types';
import useGuidedFlowStore from '../../store/guidedFlowStore';
import ContextReviewStep from './ContextReviewStep';
import { trackEvent } from '../../services/analyticsService';
import useMindspaceStore from '../../store/mindspaceStore';

/**
 * GuidedPromptFlow Component
 * 
 * This component will handle multi-step guided conversations for templates.
 * Currently a placeholder - will be fully implemented in Phase 3 (Task F3.1).
 * 
 * Features to be implemented:
 * - Multi-step question flow (max 2-3 questions per template)
 * - Session-based state management
 * - Progress indication ("Pasul 1 din 3")
 * - Flow interruption handling
 * - Context collection and storage
 */

// Helper to get the answer key used in guidedFlowStore
const getAnswerKey = (question: { canonical_key?: string; template_key?: string; is_canonical: boolean }, index: number) => {
  return question.is_canonical && question.canonical_key
    ? question.canonical_key
    : question.template_key || `question_${index}`;
};

// Helper to trim and check emptiness (we will add validation later in GF-3)
const isAnswerEmpty = (answer: string) => answer.trim().length === 0;

const TIER1_KEYS = new Set([
  'target_audience_description',
  'brand_voice',
  'product_service_description',
]);

export const GuidedPromptFlow: React.FC<GuidedPromptFlowProps> = ({
  template,
  isActive,
  onComplete,
  onCancel,
}) => {
  // Selectors from guidedFlowStore
  const getCurrentQuestion = useGuidedFlowStore((state) => state.getCurrentQuestion);
  const getProgress = useGuidedFlowStore((state) => state.getProgress);
  const canGoBackStore = useGuidedFlowStore((state) => state.canGoBack);
  const goBackStore = useGuidedFlowStore((state) => state.goBack);
  const answerQuestionStore = useGuidedFlowStore((state) => state.answerQuestion);
  const nextQuestionStore = useGuidedFlowStore((state) => state.nextQuestion);
  const answersStore = useGuidedFlowStore((state) => state.answers);
  const updateCollectedContext = useGuidedFlowStore((state) => state.updateCollectedContext);
  const mindspaceContext = useMindspaceStore((state) => state.contextItems);

  // Controlled textarea state
  const [currentAnswer, setCurrentAnswer] = useState('');

  const { current, total } = getProgress();
  const currentQuestion = getCurrentQuestion();

  // Determine if we need to show review step
  const implicitKeys = template?.implicit_context_keys ?? [];
  const [reviewDone, setReviewDone] = React.useState(implicitKeys.length === 0);

  // Fire "viewed" event for review step
  useEffect(() => {
    if (!reviewDone && isActive && implicitKeys.length > 0 && template) {
      trackEvent('guided_flow_review_step_viewed', {
        template_id: template.template_id,
        template_title: template.title,
        implicit_keys_count: implicitKeys.length,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewDone, isActive, template]);

  // Sync when question changes
  React.useEffect(() => {
    if (!currentQuestion) return;
    const key = getAnswerKey(currentQuestion, current - 1);
    setCurrentAnswer(answersStore[key] || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion]);

  // Early render condition evaluated AFTER hooks are declared
  if (!isActive || !template) {
    return null;
  }

  // If we are past review step but currentQuestion is missing, return null
  if (reviewDone && !currentQuestion) {
    return null;
  }

  // At this point, if review is done, currentQuestion is guaranteed
  const safeCurrentQuestion = currentQuestion as NonNullable<typeof currentQuestion>;

  // Validation helpers
  const isTier1Canonical = safeCurrentQuestion.is_canonical && safeCurrentQuestion.canonical_key && TIER1_KEYS.has(safeCurrentQuestion.canonical_key);
  const isAnswerValid = isTier1Canonical ? currentAnswer.trim().length >= 10 : !isAnswerEmpty(currentAnswer);

  const handleNext = () => {
    if (!isAnswerValid) return;

    // Save answer to store
    answerQuestionStore(currentAnswer);

    // Move to next question; determines if flow finished
    const hasMore = nextQuestionStore();
    if (!hasMore) {
      onComplete({ ...useGuidedFlowStore.getState().answers });
      return;
    }
  };

  const handleBack = () => {
    if (!canGoBackStore()) return;
    goBackStore();
  };

  const handleReviewContinue = (updatedValues: Record<string, string>) => {
    // Logic moved from child to parent
    // 1. Calculate modifications for analytics
    const initialValues: Record<string, string> = {};
    implicitKeys.forEach((key) => {
      const existing = mindspaceContext.find((c) => c.item_type === key);
      initialValues[key] = existing?.content || '';
    });

    let keys_modified_count = 0;
    Object.keys(updatedValues).forEach(key => {
      if (updatedValues[key].trim() !== (initialValues[key] || '').trim()) {
        keys_modified_count++;
      }
    });

    // 2. Fire analytics event
    if (template) {
      trackEvent('guided_flow_review_step_completed', {
        template_id: template.template_id,
        template_title: template.title,
        keys_modified_count,
      });
    }
    
    // 3. Persist changes to store
    Object.entries(updatedValues).forEach(([k, v]) => {
      if (v && v.trim().length > 0) {
        updateCollectedContext(k, v.trim());
      }
    });

    // 4. Mark review as done
    setReviewDone(true);
  };

  // If review not done yet, render review component
  if (!reviewDone && isActive && implicitKeys.length > 0) {
    return (
      <ContextReviewStep
        implicitKeys={implicitKeys}
        onContinue={handleReviewContinue}
        onCancel={onCancel}
      />
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.progress} aria-live="polite">
        Pasul {current} din {total}
      </div>

      <div className={styles.question}>{safeCurrentQuestion.text}</div>

      <textarea
        className={styles.input}
        value={currentAnswer}
        onChange={(e) => setCurrentAnswer(e.target.value)}
        placeholder="Răspunsul tău..."
      />

      <div className={styles.buttonRow}>
        <button
          className={`${styles.button} ${styles.secondary}`}
          onClick={handleBack}
          disabled={!canGoBackStore()}
          aria-disabled={!canGoBackStore()}
        >
          Înapoi
        </button>
        <button
          className={`${styles.button} ${styles.primary}`}
          onClick={handleNext}
          disabled={!isAnswerValid}
          aria-disabled={!isAnswerValid}
        >
          {current === total ? 'Finalizează' : 'Următorul'}
        </button>
        <button
          className={`${styles.button} ${styles.cancel}`}
          onClick={onCancel}
        >
          Renunță
        </button>
      </div>

      {/* Validation helper */}
      {isTier1Canonical && currentAnswer.trim().length > 0 && currentAnswer.trim().length < 10 && (
        <div style={{ color: 'var(--text-color-error, red)', fontSize: '0.9rem' }}>
          Răspunsul trebuie să conțină cel puțin 10 caractere.
        </div>
      )}
    </div>
  );
}; 